import { Injectable } from '@angular/core';
import { LoginFormActions, UserEffectActions } from '@elio/emanager-front/auth/action';
import { UserSelectors } from '@elio/emanager-front/auth/state';
import { HqActions } from '@elio/emanager-front/headquarter/offer/action';
import {
  CurrentOfferEffectActions,
  OfferHeaderActions,
  OfferShellActions,
  OffersEffectActions,
} from '@elio/emanager-front/offer/action';
import { HqProfessionalPushActions } from '@elio/emanager-front/professional/headquarter/action';
import { ProfessionalPushActions } from '@elio/emanager-front/professional/offer/action';
import { MessagingSubscriptionActions } from '@elio/emanager-front/push-messaging/action';
import { catchElioError } from '@elio/shared/rxjs-utils';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, mergeMap, of } from 'rxjs';
import { createHeadquarterTopic, createOfferTopic, createUserTopic } from './messaging-subscription.model';
import { MessagingSubscriptionService } from './messaging-subscription.service';

@Injectable()
export class MessagingSubscriptionEffects {
  readonly subscribeUserTopic$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LoginFormActions.loginSuccess, UserEffectActions.loadUserSuccess),
      mergeMap(({ user }) =>
        this.messagingSubscriptionService.subscribeTopic(createUserTopic(user), true).pipe(
          map(() => MessagingSubscriptionActions.subscribeToUserTopicSuccess()),
          catchElioError((error) => of(MessagingSubscriptionActions.subscribeToUserTopicFailure({ error })))
        )
      )
    );
  });

  readonly subscribeToOfferTopic$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CurrentOfferEffectActions.loadOfferSuccess),
      mergeMap(({ offer }) =>
        this.messagingSubscriptionService.subscribeTopic(createOfferTopic(offer)).pipe(
          map(() => MessagingSubscriptionActions.subscribeToOfferTopicSuccess()),
          catchElioError((error) => of(MessagingSubscriptionActions.subscribeToOfferTopicFailure({ error })))
        )
      )
    );
  });

  readonly unsubscribeToOfferTopic$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OfferShellActions.clearOffer),
      mergeMap(() =>
        this.messagingSubscriptionService.unsubscribeTopicsType('OFFER').pipe(
          map(() => MessagingSubscriptionActions.unsubscribeToOfferTopicSuccess()),
          catchElioError(() => of(MessagingSubscriptionActions.unsubscribeToOfferTopicFailure()))
        )
      )
    );
  });

  readonly subscribeToHeadquarterTopic$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HqActions.subscribeToHeadquarterTopic),
      concatLatestFrom(() => this.store.select(UserSelectors.getCurrentHeadquarter)),
      mergeMap(([, headquarter]) =>
        headquarter
          ? this.messagingSubscriptionService.subscribeTopic(createHeadquarterTopic(headquarter)).pipe(
              map(() => MessagingSubscriptionActions.subscribeToHeadquarterTopicSuccess()),
              catchElioError((error) => of(MessagingSubscriptionActions.subscribeToHeadquarterTopicFailure({ error })))
            )
          : of()
      )
    );
  });

  readonly unsubscribeToHeadquarterTopic$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HqActions.unsubscribeToHeadquarterTopic),
      mergeMap(() =>
        this.messagingSubscriptionService.unsubscribeTopicsType('HEADQUARTER').pipe(
          map(() => MessagingSubscriptionActions.unsubscribeToHeadquarterTopicSuccess()),
          catchElioError((error) => of(MessagingSubscriptionActions.unsubscribeToHeadquarterTopicFailure({ error })))
        )
      )
    );
  });

  readonly unsubscribeToAllTopics$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        OfferHeaderActions.unsubscribeBeforeLogout,
        ProfessionalPushActions.leaveUniqueOffer,
        OffersEffectActions.leaveUniqueOffer,
        HqProfessionalPushActions.leaveUniqueHeadquarter,
        OffersEffectActions.leaveUniqueHeadquarter,
        UserEffectActions.userHasNoOffersNorHqLeft
      ),
      mergeMap(() =>
        this.messagingSubscriptionService.unsubscribeAllTopics().pipe(
          map(() => MessagingSubscriptionActions.successAndLogout()),
          catchElioError(() => of(MessagingSubscriptionActions.failureAndLogout()))
        )
      )
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store,
    private messagingSubscriptionService: MessagingSubscriptionService
  ) {}
}
